import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Check, Clock } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import CreateReadingDialog from "@/components/readings/CreateReadingDialog";
import ReadingCard from "@/components/readings/ReadingCard";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";

const Readings = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data: readings, refetch } = useQuery({
    queryKey: ['readings', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('readings')
        .select(`
          id,
          type,
          reading_value,
          reading_date,
          status,
          houses (
            name
          )
        `)
        .order('reading_date', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data;
    }
  });

  // Group readings by month
  const groupedReadings = readings?.reduce((groups, reading) => {
    // Skip readings with null houses data
    if (!reading?.houses?.name) return groups;

    const date = parseISO(reading.reading_date);
    const monthYear = format(date, 'MMMM yyyy', { locale: es });
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(reading);
    return groups;
  }, {} as Record<string, typeof readings>) ?? {};

  return (
    <div className="p-4 max-w-[1600px] mx-auto md:overflow-hidden overflow-visible">
      <div className="space-y-6 md:overflow-hidden overflow-visible">
        <div className="flex md:flex-row flex-col md:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800">Estado de Lecturas</h1>
            <p className="text-muted-foreground mt-1">Monitorea el estado e historial de tus lecturas</p>
          </div>
          <div className="md:w-auto w-full">
            <CreateReadingDialog onSuccess={refetch} />
          </div>
        </div>
          
        <div className="flex justify-center mb-6">
          <ToggleGroup 
            type="single" 
            value={statusFilter} 
            onValueChange={(value) => value && setStatusFilter(value)}
            className="bg-[#F1F1F1] p-1 rounded-lg w-full md:w-auto"
          >
            <ToggleGroupItem 
              value="pending" 
              aria-label="Mostrar lecturas pendientes"
              className="flex-1 md:flex-initial px-4 md:px-8 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Pendiente
              </div>
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="completed" 
              aria-label="Mostrar lecturas completadas"
              className="flex-1 md:flex-initial px-4 md:px-8 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              <div className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />
                Completado
              </div>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <ScrollArea className="hidden md:block h-[calc(100vh-240px)]">
          <div className="space-y-8">
            {Object.entries(groupedReadings).map(([monthYear, monthReadings]) => (
              <div key={monthYear} className="space-y-4">
                <h3 className="text-lg font-semibold">
                  {monthYear.charAt(0).toUpperCase() + monthYear.slice(1)}
                </h3>
                <div className="grid gap-4">
                  {monthReadings?.map((reading) => (
                    reading?.houses?.name && (
                      <ReadingCard
                        key={reading.id}
                        id={reading.id}
                        house={reading.houses.name}
                        type={reading.type}
                        reading={reading.reading_value}
                        status={reading.status}
                        date={format(parseISO(reading.reading_date), 'd \'de\' MMMM \'de\' yyyy', { locale: es })}
                      />
                    )
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(groupedReadings).length === 0 && (
              <p className="text-center text-muted-foreground">No se encontraron lecturas</p>
            )}
          </div>
        </ScrollArea>

        <div className="md:hidden space-y-8 overflow-visible">
          {Object.entries(groupedReadings).map(([monthYear, monthReadings]) => (
            <div key={monthYear} className="space-y-4 overflow-visible">
              <h3 className="text-lg font-semibold">
                {monthYear.charAt(0).toUpperCase() + monthYear.slice(1)}
              </h3>
              <div className="grid gap-4 overflow-visible">
                {monthReadings?.map((reading) => (
                  reading?.houses?.name && (
                    <ReadingCard
                      key={reading.id}
                      id={reading.id}
                      house={reading.houses.name}
                      type={reading.type}
                      reading={reading.reading_value}
                      status={reading.status}
                      date={format(parseISO(reading.reading_date), 'd \'de\' MMMM \'de\' yyyy', { locale: es })}
                    />
                  )
                ))}
              </div>
            </div>
          ))}
          {Object.keys(groupedReadings).length === 0 && (
            <p className="text-center text-muted-foreground">No se encontraron lecturas</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Readings;